import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  Company,
  getSelectedCompany,
  storeSelectedCompany,
} from '../utils/selectedCompany';

export type { Company };

interface CompanyContextType {
  /** The company whose machines a multi-company login is currently viewing. */
  selectedCompany: Company | null;
  /** Persist and broadcast the viewed company. */
  setSelectedCompany: (company: Company | null) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(() => getSelectedCompany());

  const setSelectedCompany = useCallback((company: Company | null) => {
    storeSelectedCompany(company);
    setSelectedCompanyState(company);
  }, []);

  // Keep the selection consistent with the current login:
  //  • Machine-viewer → ensure a valid company is selected (default to the first the
  //    login was granted) BEFORE the Machines page fetches, so the very first request
  //    is already scoped. Also self-heals if the stored pick is no longer allowed.
  //  • Anyone else (super-admin, normal login, logged out) → drop any stale selection.
  useEffect(() => {
    const tenantIds: number[] =
      user && user.role !== 'SUPERADMIN' && Array.isArray(user.machineTenantIds)
        ? user.machineTenantIds.map((n: any) => Number(n)).filter((n: number) => !Number.isNaN(n))
        : [];

    if (tenantIds.length > 0) {
      const list = Array.isArray(user?.machineCompanies) ? user!.machineCompanies : [];
      const allowed = new Set(tenantIds);
      const current = getSelectedCompany();
      const currentValid = current && allowed.has(current.tenantId);
      if (!currentValid) {
        // Prefer a company with a resolved name; fall back to the raw tenant id.
        const fallback = list[0] || { tenantId: tenantIds[0], name: '' };
        setSelectedCompany(fallback);
      }
    } else if (getSelectedCompany() !== null) {
      setSelectedCompany(null);
    }
  }, [user, setSelectedCompany]);

  return (
    <CompanyContext.Provider value={{ selectedCompany, setSelectedCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
